import { useState } from 'react';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ThemePalette } from 'mui.theme';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import AssetItem from './AssetItem';
import type { productionAsset } from 'types/production.types';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  title: string;
  data: productionAsset[];
  isLoading: boolean;
  aggregation: string;
}

const options = [
  'Production',
  'Production Per Hour',
  'Utilization',
  'Fuel',
  'Idling Hours',
  'Productive Hours',
];

const sortOptions = ['Least Production', 'Most Production'];

const AssetWrapper = ({
  enableDarkTheme,
  title,
  data,
  isLoading,
  aggregation,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const [selectedSource, setSelectedSource] = useState<string>('Production');
  const [selectedSort, setSelectedSort] = useState<string>('Most Production');

  const sortedData = data?.sort((a, b) => {
    if (selectedSort === 'Least Production') {
      return a.max_load_sum - b.max_load_sum;
    }
    return b.max_load_sum - a.max_load_sum;
  });

  return (
    <Grid item lg={6} md={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.boxBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: '600',
                paddingTop: '5px',
              }}
            >
              {title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              Show
            </Typography>

            <ProductionSelector
              options={options}
              selected={selectedSource}
              setSelected={setSelectedSource}
              minWidth="150px"
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingLeft: '10px',
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              {t('sort_by_10')}
            </Typography>

            <ProductionSelector
              options={sortOptions}
              selected={selectedSort}
              setSelected={setSelectedSort}
              minWidth="150px"
            />
          </Box>
        </Box>
        <Box
          sx={{
            paddingTop: '20px',
          }}
        >
          {isLoading ? (
            <MultiColorCircularLoader height="200px" />
          ) : (
            <>
              {sortedData?.length < 1 ? (
                <Typography
                  sx={{
                    color: palette.textPrimary,
                    fontSize: '11px',
                  }}
                >
                  {t('no_assets_available_to_display')}
                </Typography>
              ) : (
                <>
                  {sortedData?.map((item, index) => {
                    return (
                      <div key={index}>
                        <AssetItem
                          data={item}
                          key={index}
                          selectedSource={selectedSource}
                          aggregation={aggregation}
                        />
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </Grid>
  );
};

export default AssetWrapper;
